export type DemoAudience = 'owner' | 'agency'

export type SalesDemoRoute = Readonly<{
  key: string
  audience: DemoAudience
  label: string
  summary: string
  path: string
  scenario: string
}>

const ownerRoutes = Object.freeze([
  { key: 'owner-contact', audience: 'owner', label: 'Lead Capture Failure', summary: 'A customer inquiry stops reaching the business.', path: '/demo/harbor-street-coffee/', scenario: 'contact' },
  { key: 'owner-website', audience: 'owner', label: 'Website Outage', summary: 'Website availability breaks and is independently rechecked.', path: '/demo/harbor-street-coffee/', scenario: 'website' },
  { key: 'owner-local', audience: 'owner', label: 'Search Visibility Change', summary: 'Local search visibility slips below its established baseline.', path: '/demo/harbor-street-coffee/', scenario: 'search' },
  { key: 'owner-lead', audience: 'owner', label: 'Lead Delivery Failure', summary: 'An accepted inquiry never becomes a lead record.', path: '/demo/lone-star-roofing/', scenario: 'delivery' },
  { key: 'owner-operations', audience: 'owner', label: 'Recurring CRM Failure', summary: 'A shared integration degrades across multiple locations.', path: '/demo/northbridge-dental/', scenario: 'pattern' },
] satisfies SalesDemoRoute[])

const agencyRoutes = Object.freeze([
  { key: 'agency-contact', audience: 'agency', label: 'Client Lead Capture Failure', summary: 'A client failure surfaces without a manual account audit.', path: '/demo/atlas-local-marketing/', scenario: 'atlas' },
  { key: 'agency-website', audience: 'agency', label: 'Client Website Outage', summary: 'A critical outage is prioritized across the portfolio.', path: '/demo/brightline-digital/', scenario: 'site' },
  { key: 'agency-lead', audience: 'agency', label: 'Client Lead Delivery Failure', summary: 'A lead journey stops downstream after submission.', path: '/demo/brightline-digital/', scenario: 'journey' },
  { key: 'agency-priority', audience: 'agency', label: 'Agency Priority Alert', summary: 'Three client problems are ordered without losing scope.', path: '/demo/brightline-digital/', scenario: 'priority' },
  { key: 'agency-verification', audience: 'agency', label: 'Verified Client Recovery', summary: 'A reported fix stays open until the evidence proves recovery.', path: '/demo/summit-growth-partners/', scenario: 'verification' },
] satisfies SalesDemoRoute[])

export const SALES_DEMO_ROUTES: Readonly<Record<DemoAudience, readonly SalesDemoRoute[]>> = Object.freeze({
  owner: ownerRoutes,
  agency: agencyRoutes,
})

export const RECOMMENDED_SALES_DEMOS: Readonly<Record<DemoAudience, SalesDemoRoute>> = Object.freeze({
  owner: ownerRoutes[0],
  agency: agencyRoutes[0],
})

export type DemoAudienceContext = Readonly<{
  ownerAppAccountId?: string
  agencyAccountId?: string
  productLanes?: readonly DemoAudience[]
  leadType?: 'BUSINESS' | 'AGENCY_PARTNER'
}>

export function resolveDemoAudience(context: DemoAudienceContext): DemoAudience | null {
  const signals = new Set<DemoAudience>()
  if (context.ownerAppAccountId) signals.add('owner')
  if (context.agencyAccountId) signals.add('agency')
  for (const lane of context.productLanes || []) signals.add(lane)
  if (context.leadType === 'BUSINESS') signals.add('owner')
  if (context.leadType === 'AGENCY_PARTNER') signals.add('agency')
  return signals.size === 1 ? [...signals][0] : null
}

export function buildSalesDemoUrl(origin: string, route: SalesDemoRoute) {
  const url = new URL(route.path, origin)
  url.searchParams.set('presentation', '1')
  url.searchParams.set('scenario', route.scenario)
  return url.toString()
}

export function isCanonicalCrmId(value?: string | null) {
  return Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))
}
